import { batch, computed, effect, signal } from "@preact/signals-react";
import { Invisible } from "../../Utilities/EntryUtils";

export const templateId = signal(null);
export const template = signal();

export const entryId = signal(null);
export const galleryValues = signal({});
export const tableValues = signal({});
export const formErrors = signal({});
export const isSaving = signal(false);
export const saveButtonText = signal("Save");
export const disabledSections = signal({});
export const entryValues = signal({});

export const showGalleryResyncWindow = signal(false);
export const showPrintPreviewWindow = signal(false);
export const entryEditMode = signal("Visual");
export const resyncGalleryName = signal("");
export const resyncGalleryDifferences = signal({});
export const isResyncingGallery = signal(false);
export const selectedTab = signal(0);
export const selectedStep = signal(0);
export const showAtlasMenu = signal(true);
export const showFilePreview = signal(false);
export const clearEntryFields = signal(false);
export const filePreviewUrl = signal("");
export const readOnlyEntry = signal(false);
export const initialCommentsLengths = signal({});
export const isEntryPreview = signal(false);

//#region Template Stages

export const steps = signal();

export const currentStageSections = computed(() => {
  return templateStageSteps.value?.length > 0
    ? template.value.sections.filter(
        (section) => section.properties.StageStep?.text === steps.value[selectedStep].label
      )
    : [];
});

export const templateStageSteps = computed(() => {
  return template.value?.properties?.StageSteps;
});

export const templateSections = computed(() => {
  if (!template.value) return [];
  return template.value?.sections;
});

const derivedTemplate = computed(() => {
  if (!template.value) return null;
  const newTemplate = JSON.tryParse(JSON.stringify(template?.value));

  newTemplate?.properties?.StageSteps?.forEach((step) => {
    const rules = newTemplate.properties?.VisibilityRules?.filter(
      (rule) => rule.stage === step.text
    );

    const visibilityResult = Invisible({
      VisibilityRules: rules,
      parameterValues: newTemplate?.properties?.ParameterValues,
    });

    let isInvisible = visibilityResult.invisible && !visibilityResult.disabled;

    if (isInvisible) {
      newTemplate.sections.forEach((section, sIndex) => {
        if (section.properties.StageStep?.text === step.text) {
          section.fields.forEach((field, fIndex) => {
            if (field.fieldType === "Checkbox") {
              newTemplate.sections[sIndex].fields[fIndex].value = false;
              entryValues.value[field.fieldName] = false;
            } else {
              newTemplate.sections[sIndex].fields[fIndex].value = "";
              entryValues.value[field.fieldName] = "";
            }
          });
        }
      });
    }
  });

  return newTemplate;
});

const derivedSteps = computed(() => {
  return derivedTemplate.value?.properties?.StageSteps?.map((step) => {
    const rules = derivedTemplate.value.properties?.VisibilityRules?.filter(
      (rule) => rule.stage === step.text
    );

    const visibilityResult = Invisible({
      VisibilityRules: rules,
      parameterValues: derivedTemplate.value.properties?.ParameterValues,
    });

    let isInvisible = visibilityResult.invisible && !visibilityResult.disabled;
    let isDisabled = visibilityResult.invisible && visibilityResult.disabled;
    let blockedStage = false;

    if (entryId.value === null) {
      blockedStage = template.value?.properties?.BlockedStagesOnNewEntry?.some(
        (stage) => stage.text === step.text
      );
    }

    if (!steps.peek()) {
      return {
        label: step.text,
        ...step,
        disabled: isDisabled || blockedStage,
        isVisible: !isInvisible,
      };
    } else {
      return {
        ...steps.peek().filter((x) => x.text === step.text)[0],
        label: step.text,
        ...step,
        disabled: isDisabled || blockedStage,
        isVisible: !isInvisible,
      };
    }
  }).filter((step) => step.isVisible);
});

effect(() => {
  const newTemplate = derivedTemplate.value;
  const newSteps = derivedSteps.value;

  batch(() => {
    if (JSON.stringify(newTemplate) !== JSON.stringify(template.value)) {
      template.value = newTemplate;
    }
    if (JSON.stringify(newSteps) !== JSON.stringify(steps.peek())) {
      steps.value = newSteps;
    }
  });
});

//#endregion Template Stages
